import { lazy, Suspense, useEffect, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { getSellOrder } from "@/services/sell-order.service";
import { useAuth } from "@/hooks/useAuth";
import { SellOrderDetail } from "../schema/sell-order.schema";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { handleExecuteSellOrderHandler, handleRejectSellOrderHandler, updateSellOrderAmountHandler } from "../schema/handler-functions";
import { SellOrderAmountAndActions } from "@/components/common/section-components/requests/sell-order/SellOrderAmountAndActions";

import { SellOrderMainDataCard } from "@/components/common/section-components/card/SellOrderMainDataCard";
import { AppColors } from "@/styles/colors";
import { SellOrderDetailLegend } from "@/components/common/section-components/requests/sell-order/SellOrderDetailLegend";
import { SellOrderMessageList } from "@/components/common/section-components/requests/sell-order/SellOrderMessageList";
import { RequestTypes } from "../../../../constants/enums";
import { useRoles } from "@/hooks/useRoles";

export const SellOrderDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.SELL_ORDERS);
  };

  const [sellOrder, setSellOrder] = useState<SellOrderDetail | null>();
  const fetchSellOrder = async (showLoad: boolean = true) => {
    if (showLoad) setLoadingSellOrder(true);
    const response = await getSellOrder({
      id: Number(id),
      usuario: user?.name ?? "",
    });

    setSellOrder(response);

    setLoadingSellOrder(false);
  };

  const [loadingSellOrder, setLoadingSellOrder] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchSellOrder();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectSellOrder, setLoadingRejectSellOrder] =
    useState<boolean>(false);
  const [loadingExecuteSellOrder, setLoadingExecuteSellOrder] =
    useState<boolean>(false);

  const handleExecuteSellOrder = async () => {
      await handleExecuteSellOrderHandler({
      navigate,
      setLoadingExecuteSellOrder,
      user,
      sellOrder,
      fetchPermissions,
    });
  };

  const handleRejectSellOrder = async (event?: FieldValues) => {
      await handleRejectSellOrderHandler({
      navigate,
      setLoadingRejectSellOrder,
      setOpenRejectModal,
      user,
      event,
      fetchPermissions,
      sellOrder,
    }); 
  };

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const { fetchPermissions } = useRoles()

  const handleEditAmount = async () => {
    updateSellOrderAmountHandler({
      errors,
      setLoadingEditAmount,
      sellOrder,
      user: user?.name ?? "",
      fetchSellOrder,
      setIsEditingAmount,
      watchedValues,
      fetchPermissions,
      navigate
    });
  };

  useEffect(() => {
    if (sellOrder && !sellOrder?.sinBase) {
      setValue("sell_order_amount", sellOrder?.montoSolicitado);
    }
  }, [sellOrder]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Orden de venta"}
        onBack={handleBack}
        backButton
        loading={loadingSellOrder}
      />

      {!loadingSellOrder && (
        <>
          <SellOrderAmountAndActions
            clearErrors={clearErrors}
            sellOrder={sellOrder}
            setIsEditingAmount={setIsEditingAmount}
            control={control}
            errors={errors}
            handleEditAmount={handleEditAmount}
            loadingEditAmount={loadingEditAmount}
            isEditingAmount={isEditingAmount}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectSellOrder={loadingRejectSellOrder}
            loadingExecuteSellOrder={loadingExecuteSellOrder}
            handleExecuteSellOrder={handleExecuteSellOrder}
            setValue={setValue}
          />

          <SellOrderMainDataCard sellOrder={sellOrder} />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <SellOrderDetailLegend />

            <SellOrderMessageList sellOrder={sellOrder} />
          </Flex>
        </>
      )}

      {sellOrder?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectSellOrder}
            loadingAccept={loadingRejectSellOrder}
            idTipoSolicitud={RequestTypes.OrdenDeVenta}
            entityName="orden de venta"
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);
